import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogContent,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { isEmail } from "class-validator";
import { useParams } from "react-router-dom";
import { getColors } from "../../themes/theme";
import axios from "axios";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { useSnackbar } from "notistack";
import { ThemeContext } from "../../context/ThemeContext";
import successIcon from "../../assets/senddialog/success.svg";
import failIcon from "../../assets/senddialog/fail.svg";

export const SendDialog = ({ open, setOpen, careplanData }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      senderInfo: "",
    },
  });

  const { darkMode } = useContext(ThemeContext);
  const { mainId, careId, subId } = useParams();
  const { token, user } = useAuthContext();
  const [loadingState, setLoadingState] = useState("idle"); // Loading state
  const [animationText, setAnimationText] = useState(""); // Animated text state
  const { enqueueSnackbar } = useSnackbar();
  const baseUrl = window.location.origin;
  const [selectedLanguage, setSelectedLanguage] = useState("English");

  useEffect(() => {
    const textOptions = [
      "Generating LifeStyle Tips",
      "Generating Citations",
      "Translating",
    ]; // Text options to display
    let textIndex = 0; // Index to track the current text
    let dotIndex = 0; // Index to track the dots
    let interval; // Interval for dots animation
    let timeout; // Timeout for switching text

    if (loadingState === "loading") {
      const showTextWithDots = () => {
        // Append dots to the current text
        setAnimationText(`${textOptions[textIndex]}${".".repeat(dotIndex)}`);
        dotIndex = (dotIndex + 1) % 4; // Cycle through 0, 1, 2, 3 dots
      };

      const switchText = () => {
        // Move to the next text in the sequence
        if (careplanData?.citationData?.length === 0 && textIndex === 1) {
          textIndex = 2; // Skip to the next text if no citations
          dotIndex -= 2;
        }
        if (textIndex < textOptions.length - 1) {
          textIndex += 1;
          dotIndex = 0; // Reset dots
        } else {
          if (careplanData?.citationData?.length > 0) {
            textIndex = 2; // Reset to the first text
            dotIndex = 0; // Reset dots
          }
        }
        // dotIndex = 0; // Reset dots
        if (textIndex < textOptions.length) {
          timeout = setTimeout(switchText, 3000); // Switch to the next text after 3 seconds
        }
      };

      // Start the dots animation
      interval = setInterval(showTextWithDots, 500); // Update dots every 500ms

      // Start switching text
      timeout = setTimeout(switchText, 3000); // Switch to the next text after 3 seconds
    } else {
      setAnimationText(""); // Reset text when not loading
    }

    return () => {
      clearInterval(interval); // Cleanup interval
      clearTimeout(timeout); // Cleanup timeout
    };
  }, [loadingState, careplanData]);

  const handlePreviewClick = async () => {
    setLoadingState("loading");
    let conditionname;
    if (careId === "modify" || careId === "copy") conditionname = mainId;
    else conditionname = `${careId}-${subId}`;

    let carePlanData = careplanData;

    if (conditionname.includes("undefined")) {
      conditionname = `${carePlanData.careplanID}`;
      if (carePlanData.careplanID === undefined) conditionname = mainId;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/generate-pdf/careplan`,
        {
          userid: user?.userid,
          conditionName: conditionname,
          practiceid: user?.practiceId,
          careplanData: carePlanData,
          language: selectedLanguage,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const fileName = response.data.fileName.slice(0, -4);

      const isMobile =
        /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      if (isMobile) {
        window.location.href = `${baseUrl}/shared_reports/${fileName}`;
      } else {
        window.open(`${baseUrl}/shared_reports/${fileName}`, "_blank");
      }

      setLoadingState("success");

      // window.open(blobUrl, "_blank");
    } catch (err) {
      console.log("Error fetching PDF:", err);
      setLoadingState("fail");
    }
  };

  const onSubmit = async (data) => {
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(data.senderInfo) && !isEmail(data.senderInfo)) {
      return;
    }
    let conditionname;
    if (careId === "modifiy" || careId === "copy") conditionname = mainId;
    else conditionname = `${careId}-${subId}`;

    let carePlanData = careplanData;

    if (conditionname.includes("undefined")) {
      conditionname = `${carePlanData.careplanID}`;
      if (carePlanData.careplanID === undefined) conditionname = mainId;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/generate-pdf/careplan`,
        {
          userid: user?.userid,
          conditionName: conditionname,
          practiceid: user?.practiceId,
          careplanData: carePlanData,
          language: selectedLanguage,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const fname = response.data.fileName.slice(0, -4);

      if (isEmail(data.senderInfo)) {
        setLoadingState("loading");
        await axios.post(
          `${process.env.REACT_APP_API_URL}/send-email/careplan`,
          {
            recipientAddress: data.senderInfo,
            content: `${user?.practiceName} recommends the following care plan.`,
            subject: `${user?.practiceName} recommends the following care plan.`,
            type: "careplan",
            fileName: fname,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else if (phoneRegex.test(data.senderInfo)) {
        setLoadingState("loading");
        await axios.post(
          `${process.env.REACT_APP_API_URL}/send-text/careplan`,
          {
            recipientAddress: data.senderInfo,
            content: `${baseUrl}/shared_reports`,
            subject: `${user?.practiceName} recommends the following care plan.`,
            type: "careplan",
            fileName: fname,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
      enqueueSnackbar("Successfully Sent!", { variant: "success" });
      setLoadingState("success");
      setOpen(false);
    } catch (err) {
      console.log("Error sending email:", err);
      setLoadingState("fail");
      enqueueSnackbar("Error sending email!", { variant: "error" });
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={() => {
        if (
          loadingState === "idle" ||
          loadingState === "success" ||
          loadingState === "fail"
        ) {
          setOpen(false);
          setLoadingState("idle");
        }
      }}
    >
      <DialogContent
        sx={{
          px: { xs: 2, sm: "20px" },
          py: { xs: 2, sm: "20px" },
          mb: 0,
          background: getColors.listItemHoverBg,
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              mb: loadingState === "idle" ? 4 : 0,
              // display: loading ? "none" : "block",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: { xs: "18px", sm: "20px" },
                  color: getColors.buttonText,
                  fontWeight: 400,
                  textAlign: "center",
                  visibility: "hidden",
                }}
              >
                Enter
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: { xs: "18px", sm: "20px" },
                  color: getColors.buttonText,
                  fontWeight: 400,
                  textAlign: "center",
                }}
              >
                Enter Patient's Email or Phone Number
              </Typography>
              <TextField
                select
                fullWidth
                variant="filled"
                value={selectedLanguage} // Bind the current language state
                onChange={(e) => setSelectedLanguage(e.target.value)} // Handle language change
                SelectProps={{
                  native: true,
                }}
                sx={{
                  width: "60px",
                  "& .MuiFilledInput-input": {
                    fontFamily: "Inter",
                    fontSize: { xs: "15px", sm: "18px" },
                    fontWeight: 500,
                    padding: { xs: "5px 5px", sm: "5px 5px" },
                    background: getColors.drawerBgLight,
                    color: getColors.secondaryTextLight,
                    borderRadius: "5px",
                  },
                }}
              >
                <option value="English">EN</option>
                <option value="Spanish">ES</option>
                <option value="French">FR</option>
              </TextField>
            </Box>
            <TextField
              fullWidth
              variant="filled"
              placeholder="Enter your email or phone number"
              disabled={loadingState === "loading"}
              sx={{
                "& .MuiFilledInput-input": {
                  fontFamily: "Inter",
                  fontSize: { xs: "15px", sm: "18px" },
                  fontWeight: 500,
                  padding: { xs: "10px 20px", sm: "14px 24px" },
                  background: getColors.drawerBgLight,
                  color: getColors.secondaryTextLight,
                  borderRadius: "5px",
                  "&.Mui-disabled": {
                    background: darkMode && "none",
                  },
                },
              }}
              error={!!errors.senderInfo}
              helperText={errors.senderInfo?.message}
              {...register("senderInfo", {
                required: "This field is required",
                validate: (value) => {
                  const phoneRegex = /^[0-9]{10}$/; // Validates 10 digits only
                  if (
                    !phoneRegex.test(value) && // Check for 10-digit phone number
                    !isEmail(value) // Check for valid email
                  ) {
                    return "Please enter a valid 10-digit US phone number or a valid email";
                  }
                  return true;
                },
              })}
            />
          </Box>

          {loadingState === "loading" && (
            <Box
              display="flex"
              justifyContent="center"
              sx={{ mt: loadingState === "loading" ? 0 : 4 }}
            >
              <Box>
                <video
                  onContextMenu={(e) => e.preventDefault()}
                  autoPlay
                  loop
                  muted
                  style={{
                    width: "300px",
                    height: "300px",
                  }}
                >
                  <source src="/assets/loading.webm" type="video/webm" />
                  Your browser does not support the video tag.
                </video>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: { xs: "16px", sm: "20px" },
                    color: getColors.buttonText,
                    fontWeight: 400,
                    textAlign: "center",
                    mb: "26px",
                  }}
                >
                  {animationText}
                </Typography>
              </Box>
            </Box>
          )}

          {loadingState === "success" && (
            <Box
              display="flex"
              justifyContent="center"
              sx={{ mt: loadingState === "loading" ? 0 : 0 }}
            >
              <Box>
                <img
                  src={successIcon}
                  alt="Success"
                  style={{
                    width: "250px",
                    height: "250px",
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: { xs: "16px", sm: "20px" },
                    color: getColors.buttonText,
                    fontWeight: 400,
                    textAlign: "center",
                    mb: "26px",
                  }}
                >
                  Successfully Generated!
                </Typography>
              </Box>
            </Box>
          )}

          {loadingState === "fail" && (
            <Box
              display="flex"
              justifyContent="center"
              sx={{ mt: loadingState === "loading" ? 0 : 4 }}
            >
              <Box>
                <img
                  src={failIcon}
                  alt="Fail"
                  style={{
                    width: "150px",
                    height: "150px",
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: { xs: "16px", sm: "20px" },
                    color: getColors.buttonText,
                    fontWeight: 400,
                    textAlign: "center",
                    my: "26px",
                  }}
                >
                  Generation Failed!
                </Typography>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              // display: loading ? "none" : "flex",
              justifyContent: "space-between",
              gap: { xs: "16px", sm: "20px" },
            }}
          >
            <Button
              sx={{
                width: "100%",
                borderRadius: "5px",
                fontFamily: "Poppins",
                fontSize: { xs: "14px", sm: "18px" },
                fontWeight: 500,
                borderWidth: 1,
                borderStyle: "solid",
                borderColor: getColors.primaryTextDark,
                color: getColors.primaryTextDark,
                py: { xs: "8px", sm: "11px" },
                textAlign: "center",
                cursor: "pointer",
                "&: hover": {
                  background: getColors.buttonText,
                  color: getColors.primaryMain,
                },
                "&.Mui-disabled": {
                  color: "#FFFFFFAA",
                },
              }}
              disabled={loadingState === "loading"}
              onClick={handlePreviewClick}
            >
              Preview
            </Button>
            <Button
              sx={{
                width: "100%",
                borderRadius: "5px",
                fontFamily: "Poppins",
                fontSize: { xs: "14px", sm: "18px" },
                fontWeight: 500,
                background: getColors.buttonText,
                color: getColors.thumbnailTextLight,
                py: { xs: "8px", sm: "11px" },
                textAlign: "center",
                cursor: "pointer",
                "&: hover": {
                  background: getColors.primaryTextDark,
                  color: getColors.primaryMain,
                },
                "&.Mui-disabled": {
                  color: "#000000AA",
                },
              }}
              type="submit"
              disabled={loadingState === "loading"}
            >
              Send
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};
